import type { GridApi, IRowNode, RowNode } from 'ag-grid-community'
import type { MileageBand } from './types'

// Helper function to update contiguous to miles
export function updateContiguousToMiles(api: GridApi<MileageBand>): Promise<void> {
  if (!api) return Promise.resolve() // Return resolved promise if api is null
  const updates: MileageBand[] = []
  const sortedNodes: RowNode<MileageBand>[] = []
  api.forEachNodeAfterFilterAndSort((rowNode: IRowNode<MileageBand>) => {
    if (rowNode.data?.key) {
      sortedNodes.push(rowNode as RowNode<MileageBand>)
    }
  })

  sortedNodes.forEach((node, index) => {
    const nextNode = sortedNodes[index + 1]
    const currentData = node.data
    if (!currentData) return

    // Determine the correct next to_mile
    let nextToMile: number | null = null // Initialize as null

    if (index === sortedNodes.length - 1) {
      // If it's the last row, set to_mile to null ONLY IF it's not already a number
      // This preserves manually set 'to_mile' via the popover
      if (typeof currentData.to_mile !== 'number') {
        nextToMile = null
      } else {
        nextToMile = currentData.to_mile // Keep existing number
      }
    } else if (nextNode?.data?.from_mile != null && nextNode.data.from_mile >= 0) {
      // If there's a valid next row, use its from_mile
      nextToMile = nextNode.data.from_mile
    } else {
      // Otherwise, keep the existing to_mile (this case might be less relevant now but kept for safety)
      nextToMile = currentData.to_mile ?? null
    }

    if (currentData.to_mile !== nextToMile) {
      const updatedRowData = {
        ...currentData,
        to_mile: nextToMile,
      }
      updates.push(updatedRowData as MileageBand)
    }
  })

  if (updates.length > 0) {
    return new Promise((resolve) => {
      // Filter out updates where to_mile would be null, as it's not allowed by the type
      const validUpdates = updates.filter((update) => update.to_mile !== null)
      const skippedUpdates = updates.filter((update) => update.to_mile === null)

      if (skippedUpdates.length > 0) {
        console.warn(
          "Skipping mileage band updates with null 'to_mile':",
          skippedUpdates.map((u) => u.key)
        )
      }

      if (validUpdates.length > 0) {
        api.applyTransactionAsync({ update: validUpdates }, () => {
          // Force refresh of the Miles column cells to show updated to_mile
          api.refreshCells({ columns: ['from_mile'], force: true })
          resolve() // Resolve the promise after the transaction and refresh
        })
      } else {
        resolve() // Resolve immediately if only invalid updates were found
      }
    })
  }
  return Promise.resolve() // Resolve immediately if no updates
}
